const KYIV_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false
};

function createKyivFormatter() {
  try {
    return new Intl.DateTimeFormat('en-GB', { ...KYIV_DATE_TIME_OPTIONS, timeZone: 'Europe/Kyiv' });
  } catch {
    // Older ICU/browser releases retain the historical name for the same IANA zone.
    return new Intl.DateTimeFormat('en-GB', { ...KYIV_DATE_TIME_OPTIONS, timeZone: 'Europe/Kiev' });
  }
}

const KYIV_DATE_TIME = createKyivFormatter();

/** Kyiv calendar fields stored in UTC slots, for calendar arithmetic independent of the browser. */
export function kyivWallTime(instant: Date): Date {
  if (!Number.isFinite(instant.getTime())) return new Date(NaN);
  const parts: Record<string, number> = {};
  KYIV_DATE_TIME.formatToParts(instant).forEach((part) => {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  });
  return new Date(
    Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour % 24, parts.minute, parts.second)
  );
}

export function formatKyivDateTime(value: string | number | Date): { date: string; time: string } {
  const instant = value instanceof Date ? value : new Date(value);
  const wallTime = kyivWallTime(instant);
  if (!Number.isFinite(wallTime.getTime())) return { date: '', time: '' };
  const text = wallTime.toISOString();
  return { date: text.slice(0, 10), time: text.slice(11, 16) };
}

/** Convert a selected Kyiv date and time to an actual instant, including the date's DST offset. */
export function kyivDateTimeToIso(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  const wallTime = new Date(`${date}T${time}:00.000Z`);
  const wallMillis = wallTime.getTime();
  if (!Number.isFinite(wallMillis) || wallTime.toISOString().slice(0, 16) !== `${date}T${time}`) {
    return null;
  }

  // Inspect both sides of a DST change. A repeated local time resolves to its first occurrence;
  // a skipped local time is invalid and must not silently move the promised delivery time.
  const offsets = new Set<number>();
  [-36, 0, 36].forEach((hours) => {
    const probe = new Date(wallMillis + hours * 3600000);
    offsets.add(kyivWallTime(probe).getTime() - probe.getTime());
  });
  const candidates = [...offsets]
    .map((offset) => new Date(wallMillis - offset))
    .filter((instant) => kyivWallTime(instant).getTime() === wallMillis)
    .sort((left, right) => left.getTime() - right.getTime());
  return candidates[0]?.toISOString() || null;
}
